import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useWallet } from '../wallet/WalletProvider';
import { apiClient } from '../../utils/api';
import { Database, TestTube, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

export function DatabaseTestPanelSimple() {
  const { account } = useWallet();
  const address = account?.address;
  const [testResults, setTestResults] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const isMockMode = apiClient.isMockMode();

  const testHealthCheck = async () => {
    setLoading(true);
    try {
      const result = await apiClient.healthCheck();
      const mode = result.mode === 'mock' ? ' (Mock Mode)' : '';
      const message = `Health Check: ${result.status === 'ok' ? 'PASSED' : 'FAILED'}${mode}`;
      setTestResults(prev => [...prev, message]);
      toast.success('Health check completed' + mode);
    } catch (error) {
      const message = `Health Check: FAILED - ${(error as Error).message}`;
      setTestResults(prev => [...prev, message]);
      toast.error('Health check failed');
    } finally {
      setLoading(false);
    }
  };

  const testDatabaseConnection = async () => {
    setLoading(true);
    try {
      // Test using the API client with mock fallback
      const result = await apiClient.healthCheck();
      const mode = result.mode === 'mock' ? ' (Mock Mode)' : '';
      const message = `Database Connection: PASSED - ${result.status}${mode}`;
      setTestResults(prev => [...prev, message]);
      toast.success('Database connection successful' + mode);
    } catch (error) {
      const message = `Database Connection: FAILED - ${(error as Error).message}`;
      setTestResults(prev => [...prev, message]);
      toast.error('Database connection failed');
    } finally {
      setLoading(false);
    }
  };

  const testProfileFetch = async () => {
    setLoading(true);
    try {
      const testAddress = address || '0x1234567890123456789012345678901234567890';
      const profile = await apiClient.getProfile(testAddress);
      const message = `Profile Fetch: PASSED - Level ${profile.level}, XP ${profile.xp}`;
      setTestResults(prev => [...prev, message]);
      toast.success('Profile fetch successful');
    } catch (error) {
      const message = `Profile Fetch: FAILED - ${(error as Error).message}`;
      setTestResults(prev => [...prev, message]);
      toast.error('Profile fetch failed');
    } finally {
      setLoading(false);
    }
  };

  const clearResults = () => {
    setTestResults([]);
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <TestTube className="h-5 w-5" />
            Database Connection Test
          </div>
          {isMockMode && (
            <Badge variant="secondary" className="text-xs">
              <AlertCircle className="h-3 w-3 mr-1" />
              Mock Mode
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {isMockMode && (
          <div className="p-4 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg">
            <p className="text-blue-800 dark:text-blue-200 text-sm">
              <strong>Demo Mode Active:</strong> The application is using mock data for demonstration. 
              All API calls will return simulated responses without connecting to a real backend.
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
          <Button 
            onClick={testHealthCheck} 
            disabled={loading}
            variant="outline"
          >
            <Database className="h-4 w-4 mr-2" />
            Health Check
          </Button>
          
          <Button 
            onClick={testDatabaseConnection} 
            disabled={loading}
            variant="outline"
          >
            <CheckCircle className="h-4 w-4 mr-2" />
            Test Connection
          </Button>

          <Button 
            onClick={testProfileFetch} 
            disabled={loading}
            variant="outline"
          >
            <Database className="h-4 w-4 mr-2" />
            Test Profile
          </Button>
          
          <Button 
            onClick={clearResults} 
            disabled={loading}
            variant="secondary"
          >
            Clear Results
          </Button>
        </div>

        {testResults.length > 0 && (
          <div className="space-y-2">
            <h4 className="font-medium">Test Results:</h4>
            <div className="p-4 bg-muted rounded-lg max-h-40 overflow-y-auto">
              {testResults.map((result, index) => (
                <div key={index} className="flex items-center gap-2 text-sm py-1">
                  {result.includes('PASSED') ? (
                    <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                  ) : (
                    <XCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
                  )}
                  <span className={result.includes('PASSED') ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}>
                    {result}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {!address && (
          <div className="p-4 bg-orange-50 dark:bg-orange-950 border border-orange-200 dark:border-orange-800 rounded-lg">
            <p className="text-orange-800 dark:text-orange-200 text-sm">
              Connect your wallet to run full database tests.
            </p>
          </div>
        )}

        <div className="text-xs text-muted-foreground">
          <p>This panel tests the basic connectivity to your Supabase database and server endpoints.</p>
          <p className="mt-1">Database URL: https://fltjcelfimjxedwouhng.supabase.co</p>
        </div>
      </CardContent>
    </Card>
  );
}